import type IRightsCollection from '@sysarb/sysarb-api/models/common/rights/RightsCollection';
import type { EventService } from '@/services/EventService';
import { Guid } from 'guid-typescript';
import type IFieldRight from '@sysarb/sysarb-api/models/common/rights/FieldRight';
import { FieldRights } from '@sysarb/sysarb-api/models/common/rights/FieldRight';
import { Common } from '@/common/Common';
import { MethodRights } from '@sysarb/sysarb-api/models/common/rights/MethodRight';
import type Entity from '@sysarb/sysarb-api/models/entities/Entity';
import type { Api } from '@sysarb/sysarb-api';
import type ApiManager from '@sysarb/sysarb-api/api/ApiManager';
import { useApi } from '@/common/ApiHelper';

export class RightsService {
    private static instance: RightsService | null = null;

    public static getInstance(): RightsService {
        RightsService.instance ??= new RightsService();
        return RightsService.instance;
    }

    private readonly rights: Map<string, Promise<IRightsCollection>> = new Map<string, Promise<IRightsCollection>>();

    /**
     * Loads and caches the current user rights for the current user and selected entity.
     * @param managerName The manager (entity) name to retrieve rights from.
     */
    public async getRights<TEntity extends Entity>(managerName: keyof Api): Promise<IRightsCollection> {
        const lowercaseEntityName = managerName.toLocaleLowerCase();

        // If the rights promise is already created return it.
        if (this.rights.has(lowercaseEntityName)) {
            return this.rights.get(lowercaseEntityName) as Promise<IRightsCollection>;
        }

        const api = useApi();

        // If it did not exist, create a new promise that returns the retrieved rights collection.
        const promise = new Promise<IRightsCollection>(async (resolve, reject) => {
            try {
                const manager = (api[managerName] as any) as ApiManager<TEntity>;
                const right: IRightsCollection = await manager.getMyRights();
                resolve(right);
            } catch (err: unknown) {
                if (err instanceof Error) {
                    reject(err);
                } else {
                    reject(new Error(`Failed to get rights for entity "${managerName}": ${err}`));
                }
            }
        });
        this.rights.set(lowercaseEntityName, promise);
        return promise;
    }

    /**
     * Gets the current user's rights for the selected field.
     * @param managerName The name of the manager (entity) to retrieve the field rights for.
     * @param fieldName The name of the field to get the rights for
     * @param withoutCondition Determines if the rights must be without a condition.
     */
    public async getFieldRights(managerName: keyof Api, fieldName: string, withoutCondition: boolean): Promise<{ rights: FieldRights, isConditional: boolean }> {
        if (managerName != null) {
            await this.getRights(managerName);
            const lowercaseEntityName = managerName.toLocaleLowerCase();
            const rights = await this.rights.get(lowercaseEntityName);
            if (rights != null) {
                const matchingRights = rights.rights.find(right => right.$type === 'FieldRight'
                    && ((right as IFieldRight).name == null || (right as IFieldRight).name.toLocaleLowerCase() === fieldName.toLocaleLowerCase())
                    && (!withoutCondition || (right as IFieldRight).condition === null)) as IFieldRight;

                if (matchingRights != null) {
                    return {
                        rights: matchingRights.right,
                        isConditional: matchingRights.condition != null
                    }; // Take the first found right, since that is the last set right (and it overwrites older ones)
                }
            }
        }

        // If no rights were found, interpret it as no rights at all
        // (this should normally not occur)
        return {
            rights: FieldRights.None,
            isConditional: false
        };
    }

    /**
     * Returns the rights the user has for the selected entity.
     * @param entity The entity to retrieve the rights from. This must be an entity recieved from the server.
     * @param fieldName The name of the field to check the rights on.
     */
    public getEntityFieldRights(entity: Entity, fieldName: string): FieldRights | null {
        // Check if the included rights on the entity are sufficient
        if (entity.entityRights != null) {
            const localeUpperCaseFieldName = Common.toUpperPascalCase(fieldName);
            const fieldRights = entity.entityRights.find(right => right.item1 === `field.${localeUpperCaseFieldName}`);

            // The rights are sufficient if the rights are at least as high as the requested rights.
            const rights = fieldRights != null ? fieldRights.item2 : null;
            return rights;
        }
        // If no rights were found, interpret as no rights at all
        return FieldRights.None;
    }

    /**
     * Returns if the user has at least the requested rights from the selected entity.
     * @param entity The entity to retrieve the rights from. This must be an entity recieved from the server.
     * @param fieldName The name of the field to check the rights on.
     * @param requestedRights The least requested rights to see if the user has.
     */
    public entityHasFieldRights(entity: Entity, fieldName: string, requestedRights: FieldRights): boolean {
        // See if the entity field rights are at least as high as the requested rights
        const fieldRights = this.getEntityFieldRights(entity, fieldName);
        const hasSufficientRights = fieldRights != null && fieldRights >= requestedRights;
        return hasSufficientRights;
    }

    /**
     * Returns the rights the user has for the selected entity.
     * @param entity The entity to retrieve the rights from. This must be an entity recieved from the server.
     * @param method The name of the method to check the rights on.
     */
    public getEntityMethodRights(entity: Entity, method: string): MethodRights | null {
        // Check if the included rights on the entity are sufficient
        if (entity.entityRights != null) {
            const localeUpperCaseFieldName = Common.toUpperPascalCase(method);
            const methodRights = entity.entityRights.find(right => right.item1 === `method.${localeUpperCaseFieldName}`);

            // The rights are sufficient if the rights are at least as high as the requested rights.
            const rights = methodRights != null ? methodRights.item2 : null;
            return rights;
        }

        // If no rights were found, interpret as no rights at all
        return MethodRights.None;
    }

    /**
     * Returns if the user has at least the requested rights from the selected entity.
     * @param entity The entity to retrieve the rights from. This must be an entity recieved from the server.
     * @param method The name of the method to check the rights on.
     * @param requestedRights The least requested rights to see if the user has.
     */
    public entityHasMethodRights(entity: Entity, method: string, requestedRights: MethodRights): boolean {
        // See if the entity field rights are at least as high as the requested rights
        const methodRights = this.getEntityMethodRights(entity, method);
        const hasSufficientRights = methodRights != null && methodRights >= requestedRights;
        return hasSufficientRights;
    }

    public clearRights(): void {
        this.rights.clear();
    }

    public removeRightsFromCache(entityName: string): void {
        const lowercaseEntityName = entityName.toLocaleLowerCase();
        this.rights.delete(lowercaseEntityName);
    }

    public initRightsEventSubscriptions(eventService: EventService): void {
        const defaultId = Guid.createEmpty();
        eventService.subscribe({
            componentId: defaultId,
            entity: 'Right',
            addedCallback: this.clearRights,
            deletedCallback: this.clearRights,
            propertyUpdatedCallback: this.clearRights
        });
        eventService.subscribe({
            componentId: defaultId,
            entity: 'Role',
            addedCallback: this.clearRights,
            deletedCallback: this.clearRights,
            propertyUpdatedCallback: this.clearRights
        });
    }
}
